// PRD §7.0.3 — first-login activation. Five sections on a single scrollable
// page (PRD: no multi-step wizard).
//
//   1. Hero plus a one-line "why you're on this list"
//   2. Profile review:
//      a. Optional LinkedIn paste with click-to-pick suggestions (the
//         "surprise the mentor, let them pick what to show" flow from the
//         PDF feedback).
//      b. Editable bio, expertise tags, and LinkedIn URL.
//   3. Four commitments — Profile reviewed, Mentor terms (TWO sub-checks:
//      one with Favcy VB and one with the Founder), Call recording,
//      Compensation framing (pro bono encouraged plus paid alternatives).
//   4. Defaults — cash hourly rate, equity preference, hours cap, and
//      Connect Google Calendar (real OAuth flow).
//   5. Activate CTA.
//
// Activation gate: the existing four timestamp columns on the mentor row.
// Profile edits and defaults are saved via PATCH /mentors/me/profile so a
// mentor who refreshes mid-flow does not lose their work.
//
// Copy guidelines (per PDF feedback): plain Indian English, no em dashes,
// no GPT clichés ("the moment we are trying to create", "seasoned",
// "passionate", "thought leader"). Numerals not words.
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import {
  Loader2, Sparkles, ShieldCheck, Mic, Coins, UserCheck,
  Check as CheckIcon, Calendar, Save, AlertCircle, Wand2, Plus,
  Link2, X,
} from "lucide-react";
import ProgressLoader from "../../components/progress-loader";

const BASE = import.meta.env.BASE_URL;

interface MentorMe {
  id: number;
  fullName: string;
  email: string;
  headline: string | null;
  bio: string | null;
  location: string | null;
  linkedinUrl: string | null;
  expertiseTags: string[] | null;
  compEquityPref: string | null;
  compCashRateInr: number | null;
  compEquityCapBps: number | null;
  hoursPerMonthCap: number | null;
  sourceOfRelationship: string | null;
  referredBy: string | null;
}

interface ProfileDraftJson {
  bio?: string;
  expertiseSummary?: string;
  workHistory?: Array<{ title: string; company: string; period: string; summary: string }>;
  publicLinks?: string[];
  bioVariants?: Array<{ label: string; text: string }>;
  expertiseTagSuggestions?: string[];
  workHighlightBullets?: string[];
}

interface ProfileDraft {
  id: number;
  draftJson: ProfileDraftJson;
  draftedAt: string;
}

interface CalendarStatus {
  available: boolean;
  connected: boolean;
  googleEmail?: string;
  calendarId?: string;
  connectedAt?: string;
}

function authHeaders(): Record<string, string> {
  const tok = localStorage.getItem("mentor_token") || "";
  return { "Content-Type": "application/json", "x-mentor-token": tok };
}

export default function MentorActivate() {
  const [, setLocation] = useLocation();
  const [mentor, setMentor] = useState<MentorMe | null>(null);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);

  // Editable profile state — hydrated from mentor + draft on load.
  const [bio, setBio] = useState("");
  const [tagsCsv, setTagsCsv] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaveErr, setProfileSaveErr] = useState<string | null>(null);

  // LinkedIn paste + drafter state.
  const [linkedinPaste, setLinkedinPaste] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draftErr, setDraftErr] = useState<string | null>(null);

  // URL-research mode (alternative to paste — uses OpenAI web_search to
  // find publicly indexed info from the LinkedIn URL + other public links).
  const [draftMode, setDraftMode] = useState<"url" | "paste">("url");
  const [researchUrl, setResearchUrl] = useState("");
  const [researchName, setResearchName] = useState("");
  const [researchOtherLinks, setResearchOtherLinks] = useState("");
  const [researchNotes, setResearchNotes] = useState("");
  const [researching, setResearching] = useState(false);
  // Track which suggestion entries the mentor has already added so we can
  // dim them — avoids "did I click that?" confusion.
  const [pickedBullets, setPickedBullets] = useState<Set<string>>(new Set());
  const [pickedTags, setPickedTags] = useState<Set<string>>(new Set());
  const [pickedBioIdx, setPickedBioIdx] = useState<number | null>(null);

  // Defaults state.
  const [cashRate, setCashRate] = useState<string>("");
  const [equityPref, setEquityPref] = useState<string>("either");
  const [equityBps, setEquityBps] = useState<string>("");
  const [hoursCap, setHoursCap] = useState<string>("");
  const [defaultsDirty, setDefaultsDirty] = useState(false);
  const [defaultsSaving, setDefaultsSaving] = useState(false);
  const [defaultsSaveErr, setDefaultsSaveErr] = useState<string | null>(null);

  // Consents. Mentor terms is split into TWO sub-checks (PDF feedback):
  // an agreement with Favcy VB AND an agreement with the Founder.
  // Both must be true for the parent terms consent to count.
  const [termsFavcy, setTermsFavcy] = useState(false);
  const [termsFounder, setTermsFounder] = useState(false);
  const terms = termsFavcy && termsFounder;
  const [recording, setRecording] = useState(false);
  const [comp, setComp] = useState(false);
  const [profileReviewed, setProfileReviewed] = useState(false);

  // Calendar OAuth state.
  const [calStatus, setCalStatus] = useState<CalendarStatus | null>(null);
  const [calBusy, setCalBusy] = useState(false);
  const [calErr, setCalErr] = useState<string | null>(null);
  const calPopupRef = useRef<Window | null>(null);
  // Watcher interval id — polls /calendar/status while the popup is open.
  // Required because postMessage from the popup CAN silently fail (Chrome's
  // Cross-Origin-Opener-Policy can null window.opener; popup may be closed
  // before posting; ad-blockers sometimes rewrite the closing HTML). Without
  // a poll we used to leave the parent stuck on "Opening..." until manual
  // refresh — exactly the bug the user hit.
  const calWatchRef = useRef<number | null>(null);

  const [activating, setActivating] = useState(false);
  const [activateErr, setActivateErr] = useState<string | null>(null);

  useEffect(() => {
    const tok = localStorage.getItem("mentor_token");
    if (!tok) { setLocation("/mentor/login"); return; }
    (async () => {
      try {
        const meR = await fetch(`${BASE}api/mentors/me`, { headers: { "x-mentor-token": tok } });
        if (!meR.ok) throw new Error("auth");
        const meD = await meR.json();
        if (!meD.needsActivation) { setLocation("/mentor"); return; }
        const m: MentorMe = meD.mentor;
        setMentor(m);

        const draftR = await fetch(`${BASE}api/mentors/me/profile-draft`, { headers: { "x-mentor-token": tok } });
        const draftD = draftR.ok ? await draftR.json() : { draft: null };
        const d: ProfileDraft | null = draftD.draft;
        setDraft(d);

        // Prefill bio: mentor.bio takes precedence over draft (mentor may
        // have already saved an edit in a previous session).
        setBio(m.bio || d?.draftJson?.bio || "");
        setTagsCsv((m.expertiseTags || []).join(", "));
        setLinkedin(m.linkedinUrl || "");
        setCashRate(m.compCashRateInr != null ? String(m.compCashRateInr) : "");
        setEquityPref(m.compEquityPref || "either");
        setEquityBps(m.compEquityCapBps != null ? String(m.compEquityCapBps) : "");
        setHoursCap(m.hoursPerMonthCap != null ? String(m.hoursPerMonthCap) : "");

        // Calendar status — fail-soft, don't block the page on this fetch.
        try {
          const csR = await fetch(`${BASE}api/mentors/me/calendar/status`, { headers: { "x-mentor-token": tok } });
          if (csR.ok) setCalStatus(await csR.json());
        } catch { /* ignore */ }
      } catch {
        setLocation("/mentor/login");
      }
    })();
  }, [setLocation]);

  // Listen for the OAuth popup posting back its result.
  // Security (architect-flagged): verify ev.origin matches our own origin
  // AND ev.source is the popup window we actually opened. Without these
  // checks any cross-origin frame on the page could fake a "connected"
  // message and trick us into refreshing status.
  useEffect(() => {
    function onMsg(ev: MessageEvent) {
      if (ev.origin !== window.location.origin) return;
      if (calPopupRef.current && ev.source !== calPopupRef.current) return;
      if (!ev.data || ev.data.type !== "mentor-calendar-oauth") return;
      const p = ev.data.payload || {};
      if (p.ok) {
        setCalErr(null);
        void refreshCalendarStatus();
      } else {
        setCalErr(p.error || "Could not connect calendar.");
      }
      setCalBusy(false);
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  async function refreshCalendarStatus(): Promise<void> {
    const tok = localStorage.getItem("mentor_token") || "";
    try {
      const r = await fetch(`${BASE}api/mentors/me/calendar/status`, { headers: { "x-mentor-token": tok } });
      if (r.ok) setCalStatus(await r.json());
    } catch { /* ignore */ }
  }

  async function startCalendarConnect(): Promise<void> {
    // Architect-flagged: a rapid double-click would open two popups and
    // leave calPopupRef pointing at the second while the first is still
    // open and might also post back. Bail early if a connect is already
    // in flight.
    if (calBusy) return;
    // Architect-flagged: opening the popup AFTER an async fetch is treated
    // as non-user-initiated by Chrome / Safari and gets blocked. Open a
    // blank popup synchronously inside the click handler first, then
    // navigate it to the OAuth URL once the fetch returns.
    setCalErr(null); setCalBusy(true);
    // Belt-and-braces: clear any prior watcher that may still be ticking
    // from a previous attempt that timed out without clearing itself.
    if (calWatchRef.current != null) { window.clearInterval(calWatchRef.current); calWatchRef.current = null; }
    try { calPopupRef.current?.close(); } catch { /* noop */ }
    const popup = window.open("about:blank", "favcy-cal-oauth", "width=540,height=720,menubar=no,toolbar=no");
    if (!popup) {
      setCalErr("Please allow popups for this site and try again.");
      setCalBusy(false);
      return;
    }
    calPopupRef.current = popup;
    try {
      const r = await fetch(`${BASE}api/mentors/me/calendar/oauth-url`, { headers: authHeaders() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setCalErr(d.error || "Could not start the connect flow.");
        try { popup.close(); } catch { /* noop */ }
        setCalBusy(false);
        return;
      }
      const { url } = await r.json();
      try { popup.location.replace(url); } catch {
        // If the popup was blocked or closed by the user mid-flight.
        setCalErr("The connect window was closed. Please try again.");
        setCalBusy(false);
        return;
      }
      // Resilient watcher — see calWatchRef comment above for why postMessage
      // alone is not enough. Polls every 2.5s for up to 2 minutes:
      //   • if status flips to connected → clear the spinner immediately
      //   • if popup is closed without success → clear the spinner so the
      //     mentor can retry instead of being stuck on "Opening..."
      if (calWatchRef.current != null) { window.clearInterval(calWatchRef.current); calWatchRef.current = null; }
      const startedAt = Date.now();
      const tok = localStorage.getItem("mentor_token") || "";
      calWatchRef.current = window.setInterval(async () => {
        try {
          const sr = await fetch(`${BASE}api/mentors/me/calendar/status`, { headers: { "x-mentor-token": tok } });
          if (sr.ok) {
            const s = await sr.json();
            setCalStatus(s);
            if (s.connected) {
              if (calWatchRef.current != null) { window.clearInterval(calWatchRef.current); calWatchRef.current = null; }
              setCalBusy(false);
              setCalErr(null);
              try { calPopupRef.current?.close(); } catch { /* noop */ }
              return;
            }
          }
        } catch { /* ignore — keep polling */ }
        const closed = !calPopupRef.current || calPopupRef.current.closed;
        if (closed) {
          if (calWatchRef.current != null) { window.clearInterval(calWatchRef.current); calWatchRef.current = null; }
          setCalBusy(false);
          return;
        }
        if (Date.now() - startedAt > 120_000) {
          if (calWatchRef.current != null) { window.clearInterval(calWatchRef.current); calWatchRef.current = null; }
          setCalBusy(false);
          setCalErr("Took too long. If you finished connecting, refresh the page; otherwise try again.");
        }
      }, 2500);
    } catch {
      try { popup.close(); } catch { /* noop */ }
      setCalErr("Network error. Please try again.");
      setCalBusy(false);
    }
  }

  // Cleanup the watcher if the user navigates away mid-flight.
  useEffect(() => {
    return () => {
      if (calWatchRef.current != null) { window.clearInterval(calWatchRef.current); calWatchRef.current = null; }
    };
  }, []);

  async function disconnectCalendar(): Promise<void> {
    setCalBusy(true); setCalErr(null);
    try {
      await fetch(`${BASE}api/mentors/me/calendar/disconnect`, { method: "POST", headers: authHeaders() });
      await refreshCalendarStatus();
    } catch {
      setCalErr("Could not disconnect. Please try again.");
    } finally {
      setCalBusy(false);
    }
  }

  async function runResearch() {
    if (researching || drafting) return;
    const url = researchUrl.trim();
    if (!/^https?:\/\/(www\.|in\.)?linkedin\.com\/in\/[^\s]+/i.test(url)) {
      setDraftErr("Please paste a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/your-handle).");
      return;
    }
    setResearching(true); setDraftErr(null);
    try {
      const otherLinks = researchOtherLinks
        .split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      const r = await fetch(`${BASE}api/mentors/me/profile-research`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          linkedinUrl: url,
          fullName: researchName.trim() || undefined,
          otherLinks,
          freeformNotes: researchNotes.trim() || undefined,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setDraftErr(d.error || "Could not research that profile. Please try again, or paste your text below.");
        return;
      }
      const d = await r.json();
      setDraft(d.draft);
      setPickedBullets(new Set());
      setPickedTags(new Set());
      setPickedBioIdx(null);
      // If the mentor hasn't filled in their LinkedIn URL field below yet,
      // copy it from the research URL — saves a step.
      if (!linkedin.trim()) { setLinkedin(url); setProfileDirty(true); }
    } catch {
      setDraftErr("Network error. Please try again.");
    } finally {
      setResearching(false);
    }
  }

  async function runDrafter() {
    if (drafting) return;
    if (linkedinPaste.trim().length < 60) {
      setDraftErr("Please paste at least a couple of lines from your LinkedIn About or Experience section.");
      return;
    }
    setDrafting(true); setDraftErr(null);
    try {
      const r = await fetch(`${BASE}api/mentors/me/profile-draft`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ linkedinPaste }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setDraftErr(d.error || "Could not draft suggestions. Please try again.");
        return;
      }
      const d = await r.json();
      setDraft(d.draft);
      setPickedBullets(new Set());
      setPickedTags(new Set());
      setPickedBioIdx(null);
    } catch {
      setDraftErr("Network error. Please try again.");
    } finally {
      setDrafting(false);
    }
  }

  // Click handlers for the pickable suggestions.
  function pickBio(idx: number, text: string) {
    setBio(text); setPickedBioIdx(idx); setProfileDirty(true);
  }
  function appendBullet(b: string) {
    if (pickedBullets.has(b)) return;
    const next = new Set(pickedBullets); next.add(b); setPickedBullets(next);
    setBio((cur) => {
      const trimmed = cur.trim();
      if (!trimmed) return b;
      // Avoid duplicating a sentence that's already there verbatim.
      if (trimmed.includes(b)) return cur;
      return `${trimmed}\n${b}`;
    });
    setProfileDirty(true);
  }
  function pickTag(t: string) {
    if (pickedTags.has(t)) return;
    const next = new Set(pickedTags); next.add(t); setPickedTags(next);
    const cur = tagsCsv.trim();
    const exists = cur.split(",").map((x) => x.trim().toLowerCase()).includes(t.toLowerCase());
    if (!exists) {
      setTagsCsv(cur ? `${cur}, ${t}` : t);
      setProfileDirty(true);
    }
  }
  function pickLink(u: string) {
    setBio((cur) => {
      if (cur.includes(u)) return cur;
      return cur ? `${cur}\n${u}` : u;
    });
    setProfileDirty(true);
  }

  const saveProfile = useCallback(async () => {
    if (!mentor || profileSaving) return;
    setProfileSaving(true); setProfileSaveErr(null);
    try {
      const tags = tagsCsv.split(",").map(t => t.trim()).filter(Boolean);
      const body: any = { bio, expertiseTags: tags, linkedinUrl: linkedin };
      if (draft) body.applyDraftId = draft.id;
      const r = await fetch(`${BASE}api/mentors/me/profile`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setProfileSaveErr(d.error || "Save failed"); return;
      }
      setProfileDirty(false);
    } catch { setProfileSaveErr("Network error"); }
    finally { setProfileSaving(false); }
  }, [mentor, bio, tagsCsv, linkedin, draft, profileSaving]);

  const saveDefaults = useCallback(async () => {
    if (!mentor || defaultsSaving) return;
    setDefaultsSaving(true); setDefaultsSaveErr(null);
    try {
      const body: any = { compEquityPref: equityPref };
      if (cashRate.trim()) body.compCashRateInr = Number(cashRate);
      if (equityBps.trim()) body.compEquityCapBps = Number(equityBps);
      if (hoursCap.trim()) body.hoursPerMonthCap = Number(hoursCap);
      const r = await fetch(`${BASE}api/mentors/me/profile`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setDefaultsSaveErr(d.error || "Save failed"); return;
      }
      setDefaultsDirty(false);
    } catch { setDefaultsSaveErr("Network error"); }
    finally { setDefaultsSaving(false); }
  }, [mentor, cashRate, equityPref, equityBps, hoursCap, defaultsSaving]);

  const allConsents = terms && recording && comp && profileReviewed;
  // 5 ticks total because Mentor Terms is two sub-ticks (Favcy + Founder).
  const consentTotal = 5;
  const consentCount =
    [termsFavcy, termsFounder, recording, comp, profileReviewed].filter(Boolean).length;

  async function activate() {
    if (!allConsents || activating) return;
    if (profileDirty) await saveProfile();
    if (defaultsDirty) await saveDefaults();

    setActivating(true); setActivateErr(null);
    try {
      const r = await fetch(`${BASE}api/mentors/me/activate`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          acceptedTerms: terms, acceptedRecording: recording,
          acceptedCompFraming: comp, profileReviewed: profileReviewed,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setActivateErr(d.error || "Activation failed"); return; }
      setLocation("/mentor");
    } catch { setActivateErr("Network error"); }
    finally { setActivating(false); }
  }

  if (!mentor) {
    return (
      <div data-mentor-theme="dark" className="min-h-screen bg-[var(--mentor-bg)] text-[var(--mentor-fg)] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#FF5C00]" />
      </div>
    );
  }

  const firstName = mentor.fullName.split(" ")[0] || mentor.fullName;
  const personalLine = mentor.referredBy
    ? `Nominated by ${mentor.referredBy}.`
    : (mentor.sourceOfRelationship || "Hand-picked by the Favcy partnerships team.");

  const dj: ProfileDraftJson = draft?.draftJson || {};
  const bioVariants = dj.bioVariants || [];
  const tagSuggestions = dj.expertiseTagSuggestions || [];
  const bullets = dj.workHighlightBullets || [];
  const links = dj.publicLinks || [];

  return (
    <div data-mentor-theme="dark" className="min-h-screen bg-[var(--mentor-bg)] text-[var(--mentor-fg)] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-[820px] h-[820px] rounded-full bg-[#FF5C00]/[0.07] blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF5C00]/[0.04] blur-3xl" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 py-10 lg:py-16 space-y-12">
        {/* Brand bar */}
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-[#FF5C00] flex items-center justify-center text-white text-xs font-bold">F</div>
          <div>
            <div className="text-sm font-bold tracking-tight">Favcy Select</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Mentor activation</div>
          </div>
        </div>

        {/* SECTION 1 — Hero */}
        <section data-testid="section-hero">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-[#FF5C00] border border-[#FF5C00]/30 rounded-full px-2.5 py-1 mb-5">
            <Sparkles className="w-3 h-3" /> Welcome, {firstName}
          </div>
          <h1 className="text-3xl lg:text-4xl font-serif font-bold tracking-tight leading-[1.1] mb-5">
            You are in. A few quick things before you start.
          </h1>
          <p className="text-base text-white/75 leading-relaxed mb-6">
            Favcy Select is a media-led venture builder. We back early founders end to end with code-grounded AI agents and a small senior team. We do not run a generic mentor marketplace. Every engagement here is tied to a real founder problem with a real deadline.
          </p>
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-medium mb-1.5">Why you</div>
            <p className="text-sm text-white/80 leading-relaxed">{personalLine}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
            <Pillar title="Real problems." body="Each mission is a founder with a real deadline. No tyre kickers." />
            <Pillar title="Recorded terms." body="Comp is agreed at the start. Settlement happens offline through Favcy ops with a full audit trail." />
            <Pillar title="Optional reputation." body="You can opt in to a public Favcy mentor profile and reference your work here." />
          </div>
        </section>

        {/* SECTION 2 — Profile */}
        <section data-testid="section-profile">
          <SectionHeader
            num={2}
            title="Your profile"
            subtitle="Tell founders who you are. This is what they see before reaching out."
          />

          {/* Pickable suggestions block — surprise UX from the PDF feedback. */}
          <div className="rounded-xl border border-[#FF5C00]/20 bg-[#FF5C00]/[0.04] p-4 mb-6">
            <div className="flex items-start gap-2.5 mb-3">
              <Wand2 className="w-4 h-4 text-[#FF5C00] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Save typing. Let us draft from LinkedIn.</div>
                <p className="text-xs text-white/65 mt-1 leading-relaxed">
                  Two ways. Pick whichever is easier: give us your LinkedIn URL and we will research the publicly indexed parts of your profile, OR paste your About and Experience text directly. Either way, you will see a few bio versions, expertise tags, and one-line highlights as clickable cards. Nothing is saved until you click Save.
                </p>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="inline-flex rounded-lg border border-white/10 p-0.5 mb-3 bg-black/30">
              <button
                type="button"
                onClick={() => { setDraftMode("url"); setDraftErr(null); }}
                data-testid="btn-mode-url"
                className={`text-xs font-medium px-3 py-1.5 rounded-md transition ${draftMode === "url" ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"}`}
              >
                Use LinkedIn URL
              </button>
              <button
                type="button"
                onClick={() => { setDraftMode("paste"); setDraftErr(null); }}
                data-testid="btn-mode-paste"
                className={`text-xs font-medium px-3 py-1.5 rounded-md transition ${draftMode === "paste" ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"}`}
              >
                Paste text instead
              </button>
            </div>

            {draftMode === "url" && (
              <div className="space-y-2.5">
                <input
                  type="url"
                  value={researchUrl}
                  onChange={(e) => setResearchUrl(e.target.value)}
                  data-testid="input-research-url"
                  placeholder="https://www.linkedin.com/in/your-handle"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
                />
                <input
                  type="text"
                  value={researchName}
                  onChange={(e) => setResearchName(e.target.value)}
                  data-testid="input-research-name"
                  placeholder="Your full name (helps us find the right profile)"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
                />
                <textarea
                  value={researchOtherLinks}
                  onChange={(e) => setResearchOtherLinks(e.target.value)}
                  rows={2}
                  data-testid="input-research-other-links"
                  placeholder="Other public links — your firm site, X / Twitter, interviews, articles. One per line."
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
                />
                <textarea
                  value={researchNotes}
                  onChange={(e) => setResearchNotes(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  data-testid="input-research-notes"
                  placeholder="Anything you want us to highlight (optional). For example: focus on your operator background, not your investing work."
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={runResearch}
                    disabled={researching || drafting || !researchUrl.trim()}
                    data-testid="btn-run-research"
                    className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-[#FF5C00] text-white hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {researching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    {researching ? "Researching..." : draft ? "Re-research" : "Research from LinkedIn"}
                  </button>
                  <span className="text-[11px] text-white/45">
                    We search publicly indexed pages only. Every claim is verified against what we found before showing it to you.
                  </span>
                </div>
                {researching && (
                  <div className="mt-3">
                    <ProgressLoader
                      active={researching}
                      title="Researching your public profile"
                      stages={[
                        "Searching for your LinkedIn page snippets",
                        "Searching for interviews, firm sites, articles",
                        "Pulling out concrete facts (no guessing)",
                        "Drafting two or three bio versions",
                        "Verifying every claim against what we actually found",
                      ]}
                    />
                  </div>
                )}
              </div>
            )}

            {draftMode === "paste" && (
              <div>
                <p className="text-[11px] text-white/55 mb-2 leading-relaxed">
                  Open your LinkedIn profile, select the text from your <strong>About</strong> and <strong>Experience</strong> sections, and paste it below.
                </p>

            <textarea
              value={linkedinPaste}
              onChange={(e) => setLinkedinPaste(e.target.value)}
              rows={5}
              maxLength={12000}
              data-testid="input-linkedin-paste"
              placeholder="Paste your LinkedIn About and Experience here..."
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60 mb-2"
            />

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={runDrafter}
                disabled={drafting || linkedinPaste.trim().length < 60}
                data-testid="btn-run-drafter"
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-[#FF5C00] text-white hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {drafting ? "Reading..." : draft ? "Re-draft suggestions" : "Suggest from LinkedIn"}
              </button>
              <span className="text-[11px] text-white/50">
                {linkedinPaste.trim().length}/12000
              </span>
              {(() => {
                const t = linkedinPaste.trim();
                if (!t) return null;
                const looksUrlOnly = /^https?:\/\/\S+$/i.test(t) || /^(www\.)?linkedin\.com\/\S*$/i.test(t);
                if (looksUrlOnly) {
                  return (
                    <span className="text-[11px] text-amber-300/90" data-testid="hint-url-only">
                      That looks like a URL. Open the profile in another tab, copy the About + Experience text, and paste it here.
                    </span>
                  );
                }
                if (t.length < 60) {
                  return (
                    <span className="text-[11px] text-white/55">
                      Paste at least a few sentences from About + Experience to get useful suggestions.
                    </span>
                  );
                }
                return null;
              })()}
            </div>

            {drafting && (
              <div className="mt-4">
                <ProgressLoader
                  active={drafting}
                  title="Reading your LinkedIn paste"
                  stages={[
                    "Parsing the paste",
                    "Pulling out concrete facts (no embellishment)",
                    "Drafting two or three bio versions",
                    "Picking expertise tags from your roles",
                    "Putting the best one-liners forward",
                  ]}
                  stageIntervalMs={3500}
                />
              </div>
            )}
              </div>
            )}

            {draftErr && <div className="text-xs text-red-400 mt-3">{draftErr}</div>}

            {/* Pickable suggestions render below once a draft exists. */}
            {draft && !drafting && (
              <div className="mt-5 space-y-5">
                {bioVariants.length > 0 && (
                  <PickGroup title="Bio — pick one to start" hint="Click a card to drop it into the bio field below. You can edit after.">
                    <div className="grid grid-cols-1 gap-2.5">
                      {bioVariants.map((v, i) => {
                        const isPicked = pickedBioIdx === i;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => pickBio(i, v.text)}
                            data-testid={`pick-bio-${i}`}
                            className={`text-left rounded-lg border p-3 transition ${
                              isPicked
                                ? "border-[#FF5C00]/60 bg-[#FF5C00]/10"
                                : "border-white/12 bg-black/30 hover:border-white/30 hover:bg-black/40"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[10px] uppercase tracking-[0.16em] font-medium text-[#FF5C00]">{v.label}</span>
                              {isPicked && <span className="text-[10px] text-emerald-400 inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> Picked</span>}
                            </div>
                            <p className="text-xs text-white/80 leading-relaxed whitespace-pre-line">{v.text}</p>
                          </button>
                        );
                      })}
                    </div>
                  </PickGroup>
                )}

                {tagSuggestions.length > 0 && (
                  <PickGroup title="Expertise tags — click any that fit" hint="Each click adds the tag to the field below.">
                    <div className="flex flex-wrap gap-1.5">
                      {tagSuggestions.map((t) => {
                        const isPicked = pickedTags.has(t);
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => pickTag(t)}
                            disabled={isPicked}
                            data-testid={`pick-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition ${
                              isPicked
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 cursor-default"
                                : "border-white/15 bg-black/25 text-white/80 hover:border-[#FF5C00]/50 hover:text-white"
                            }`}
                          >
                            {isPicked ? <CheckIcon className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </PickGroup>
                )}

                {bullets.length > 0 && (
                  <PickGroup title="One-line highlights — click any to drop into your bio" hint="Useful if you want to keep the bio crisp and add a single specific line at the end.">
                    <div className="space-y-1.5">
                      {bullets.map((b) => {
                        const isPicked = pickedBullets.has(b);
                        return (
                          <button
                            key={b}
                            type="button"
                            onClick={() => appendBullet(b)}
                            disabled={isPicked}
                            data-testid={`pick-bullet`}
                            className={`block w-full text-left text-xs rounded border px-3 py-2 transition ${
                              isPicked
                                ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300/80 cursor-default"
                                : "border-white/10 bg-black/25 text-white/75 hover:border-white/25 hover:text-white"
                            }`}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              {isPicked ? <CheckIcon className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                              {b}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </PickGroup>
                )}

                {links.length > 0 && (
                  <PickGroup title="Public links found" hint="Click to append to your bio. Edit before saving if you want a label.">
                    <div className="flex flex-wrap gap-1.5">
                      {links.map((u) => (
                        <button
                          key={u}
                          type="button"
                          onClick={() => pickLink(u)}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-white/15 bg-black/25 text-white/75 hover:border-[#FF5C00]/40 hover:text-white truncate max-w-[260px]"
                        >
                          <Link2 className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{u}</span>
                        </button>
                      ))}
                    </div>
                  </PickGroup>
                )}
              </div>
            )}
          </div>

          <Field label="Short bio (3 to 4 sentences)">
            <textarea
              value={bio}
              onChange={(e) => { setBio(e.target.value); setProfileDirty(true); }}
              rows={5}
              maxLength={2000}
              data-testid="input-bio"
              placeholder="What you have built, who you have helped, what you can help a founder with."
              className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
            />
          </Field>

          <Field label="Expertise tags (comma separated)">
            <input
              type="text" value={tagsCsv}
              onChange={(e) => { setTagsCsv(e.target.value); setProfileDirty(true); }}
              maxLength={400}
              data-testid="input-tags"
              placeholder="e.g. B2B SaaS GTM, Pricing, Series A fundraising"
              className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
            />
          </Field>

          <Field label="LinkedIn URL">
            <input
              type="url" value={linkedin}
              onChange={(e) => { setLinkedin(e.target.value); setProfileDirty(true); }}
              maxLength={500}
              data-testid="input-linkedin"
              placeholder="https://linkedin.com/in/yourname"
              className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
            />
          </Field>

          <div className="flex items-center gap-3 mt-3">
            <button
              type="button" onClick={saveProfile}
              disabled={!profileDirty || profileSaving}
              data-testid="btn-save-profile"
              className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-[#FF5C00] text-white hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {profileSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {profileSaving ? "Saving..." : profileDirty ? "Save profile" : "Saved"}
            </button>
            {profileSaveErr && <div className="text-xs text-red-400">{profileSaveErr}</div>}
          </div>
        </section>

        {/* SECTION 3 — Commitments */}
        <section data-testid="section-consents">
          <SectionHeader num={3} title="Your commitments" subtitle="Five quick ticks. About a minute." />

          <div className="mb-5 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#FF5C00] transition-all duration-500" style={{ width: `${(consentCount / consentTotal) * 100}%` }} />
            </div>
            <div className="text-xs font-medium text-white/60 tabular-nums">{consentCount} / {consentTotal}</div>
          </div>

          <ConsentCard
            checked={profileReviewed} onToggle={() => setProfileReviewed(!profileReviewed)}
            icon={<UserCheck className="w-4 h-4" />}
            title="Profile reviewed"
            tagline="What founders see is what you have approved."
            testId="check-profile"
            confirmLabel="I have reviewed my bio, tags, and links above. They represent me accurately."
          >
            <p className="text-sm text-white/70 leading-relaxed">
              Anything wrong? Email <a href="mailto:pc@favcy.in" className="text-[#FF5C00] hover:underline">pc@favcy.in</a> before you activate. We will fix it before any founder sees you.
            </p>
          </ConsentCard>

          {/* Mentor terms — split into TWO sub-checks per PDF feedback. */}
          <div
            className={`relative rounded-xl border transition mb-3 ${
              terms
                ? "bg-[#FF5C00]/[0.05] border-[#FF5C00]/35"
                : "bg-[var(--mentor-card)] border-[var(--mentor-border)] hover:border-white/20"
            }`}
          >
            <div className="p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition ${
                  terms ? "bg-[#FF5C00] text-white" : "bg-[#FF5C00]/10 border border-[#FF5C00]/25 text-[#FF5C00]"
                }`}>
                  {terms ? <CheckIcon className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold leading-tight">Mentor terms</h3>
                  <p className="text-xs text-white/60 mt-1 italic">There are two parties on every engagement. Please tick both.</p>
                </div>
              </div>

              <div className="ml-11 space-y-3">
                <p className="text-sm text-white/70 leading-relaxed">
                  Favcy Select facilitates the introduction. The actual engagement is between you and the founder, but Favcy holds the recording, the audit trail, and settles any cash or equity offline. So there are two separate agreements you are accepting:
                </p>

                <SubConsent
                  checked={termsFavcy}
                  onToggle={() => setTermsFavcy(!termsFavcy)}
                  testId="check-terms-favcy"
                  title="Agreement with Favcy VB"
                  body="I agree to Favcy Venture Builder's mentor terms — recording is held by Favcy, conduct on the platform is governed by Favcy's policies, and settlement of any cash or equity passes through Favcy ops with full audit trail."
                />

                <SubConsent
                  checked={termsFounder}
                  onToggle={() => setTermsFounder(!termsFounder)}
                  testId="check-terms-founder"
                  title="Agreement with the Founder"
                  body="I agree that on each engagement I will state my hours, scope, and comp upfront. The founder will see and accept these terms before kickoff. The actual mentoring relationship is between me and the founder."
                />
              </div>
            </div>
          </div>

          <ConsentCard
            checked={recording} onToggle={() => setRecording(!recording)}
            icon={<Mic className="w-4 h-4" />}
            title="Call recording"
            tagline="DPDP compliant. For your reference, not for surveillance."
            testId="check-recording"
            confirmLabel="I consent to platform-mediated calls being recorded for guidance archival."
          >
            <p className="text-sm text-white/70 leading-relaxed">
              All calls scheduled through the platform are recorded under your mentor agreement (DPDP 2023 and IT Act compliant). Recording is owned by Favcy and made available to you in your engagement workspace. Google Meet's native banner shows when recording starts.
            </p>
          </ConsentCard>

          <ConsentCard
            checked={comp} onToggle={() => setComp(!comp)}
            icon={<Coins className="w-4 h-4" />}
            title="Compensation framing"
            tagline="Pro bono is the default. Paid is fully supported when you want it."
            testId="check-comp"
            confirmLabel="I understand the comp framing — pro bono is welcome, and any paid terms agreed at engagement start are recorded and settled offline by Favcy ops."
          >
            <div className="text-sm text-white/70 leading-relaxed space-y-2">
              <p>
                We strongly encourage pro bono engagements at Favcy Select. Most of our best work happens because someone senior decided to help a founder for free. If you choose pro bono, you do not need to fill the rate or equity fields below. Just pick the missions you want to help on.
              </p>
              <p>
                If you would rather take paid engagements, that is also fully supported. You can set a cash hourly rate, an equity preference (cash, equity, or either), and an equity cap per mission in the Defaults section below. The platform records what was agreed at the start of an engagement. It does not execute payments or grants. Favcy ops settles offline against the audit trail and posts the settlement record back into the platform.
              </p>
            </div>
          </ConsentCard>
        </section>

        {/* SECTION 4 — Defaults */}
        <section data-testid="section-defaults">
          <SectionHeader num={4} title="Your defaults" subtitle="Optional. Per-mission terms can override these later. Skip if you want to mentor pro bono." />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Cash hourly rate (INR)">
              <input
                type="number" min="0" value={cashRate}
                onChange={(e) => { setCashRate(e.target.value); setDefaultsDirty(true); }}
                data-testid="input-cash-rate"
                placeholder="e.g. 5000"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
              />
            </Field>

            <Field label="Equity preference">
              <select
                value={equityPref}
                onChange={(e) => { setEquityPref(e.target.value); setDefaultsDirty(true); }}
                data-testid="input-equity-pref"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#FF5C00]/60"
              >
                <option value="cash">Cash only</option>
                <option value="equity">Equity only</option>
                <option value="either">Either or mixed</option>
              </select>
            </Field>

            <Field label="Equity cap per mission (basis points, 1bp = 0.01%)">
              <input
                type="number" min="0" max="10000" value={equityBps}
                onChange={(e) => { setEquityBps(e.target.value); setDefaultsDirty(true); }}
                data-testid="input-equity-bps"
                placeholder="e.g. 25 (= 0.25%)"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
              />
            </Field>

            <Field label="Hours per month cap">
              <input
                type="number" min="0" value={hoursCap}
                onChange={(e) => { setHoursCap(e.target.value); setDefaultsDirty(true); }}
                data-testid="input-hours-cap"
                placeholder="e.g. 8"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF5C00]/60"
              />
            </Field>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              type="button" onClick={saveDefaults}
              disabled={!defaultsDirty || defaultsSaving}
              data-testid="btn-save-defaults"
              className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-[#FF5C00] text-white hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {defaultsSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {defaultsSaving ? "Saving..." : defaultsDirty ? "Save defaults" : "Saved"}
            </button>
            {defaultsSaveErr && <div className="text-xs text-red-400">{defaultsSaveErr}</div>}
          </div>

          {/* Connect Google Calendar — real OAuth (Chunk 4). Falls back to a
              clear "not yet provisioned" affordance if env vars are missing
              on the server. */}
          <div className="mt-5 rounded-lg border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-white/60 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium">Connect Google Calendar</div>
                <div className="text-xs text-white/60 mt-0.5 leading-relaxed">
                  When connected, we will write each intro and follow-up call onto your primary calendar so you do not have to chase the email invite. We only ask for calendar event and free-busy access. We will never read existing events you did not create through Favcy.
                </div>
              </div>
              {calStatus?.connected ? (
                <button
                  type="button"
                  onClick={disconnectCalendar}
                  disabled={calBusy}
                  data-testid="btn-cal-disconnect"
                  className="text-[11px] uppercase tracking-wide font-medium px-3 py-1.5 rounded border border-white/15 text-white/75 hover:border-white/40 hover:text-white disabled:opacity-50"
                >
                  {calBusy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <X className="w-3 h-3 inline mr-1" />}
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startCalendarConnect}
                  disabled={calBusy || !calStatus || calStatus.available === false}
                  data-testid="btn-cal-connect"
                  className="text-[11px] uppercase tracking-wide font-medium px-3 py-1.5 rounded border border-[#FF5C00]/40 text-[#FF5C00] hover:bg-[#FF5C00]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {calBusy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : null}
                  {calBusy ? "Opening..." : "Connect"}
                </button>
              )}
            </div>
            {calStatus?.connected && calStatus.googleEmail && (
              <div className="mt-3 ml-7 text-[11px] text-emerald-400 inline-flex items-center gap-1.5">
                <CheckIcon className="w-3 h-3" /> Connected as {calStatus.googleEmail}
              </div>
            )}
            {calStatus && calStatus.available === false && (
              <div className="mt-3 ml-7 text-[11px] text-white/60">
                Calendar connect is being provisioned by Favcy ops. You will be able to connect this shortly. Until then, you will get a calendar invite by email for every call.
              </div>
            )}
            {calErr && <div className="mt-3 ml-7 text-[11px] text-red-400">{calErr}</div>}
          </div>
        </section>

        {/* SECTION 5 — Activate CTA */}
        <section data-testid="section-activate" className="border-t border-white/10 pt-8">
          {!allConsents && (
            <div className="mb-4 flex items-start gap-2 text-xs text-white/65">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>Tick all five commitments above to activate. (Mentor terms is two ticks: Favcy and Founder.)</div>
            </div>
          )}
          {activateErr && (
            <div className="mb-4 text-sm text-red-400">{activateErr}</div>
          )}
          <button
            type="button" onClick={activate}
            disabled={!allConsents || activating}
            data-testid="btn-activate"
            className="w-full inline-flex items-center justify-center gap-2 text-base font-semibold px-6 py-3.5 rounded-lg bg-[#FF5C00] text-white hover:bg-[#E55200] disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {activating ? "Activating..." : "Activate and enter the network"}
          </button>
        </section>
      </div>
    </div>
  );
}

function Pillar({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3.5">
      <div className="text-sm font-semibold mb-1">{title}</div>
      <div className="text-xs text-white/65 leading-relaxed">{body}</div>
    </div>
  );
}

function SectionHeader({ num, title, subtitle }: { num: number; title: string; subtitle: string }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-medium mb-1">Section {num}</div>
      <h2 className="text-2xl font-serif font-bold tracking-tight">{title}</h2>
      <p className="text-sm text-white/65 mt-1.5">{subtitle}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block text-[11px] uppercase tracking-[0.14em] text-white/65 font-medium mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PickGroup({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-white/85 mb-1">{title}</div>
      <div className="text-[11px] text-white/60 mb-2">{hint}</div>
      {children}
    </div>
  );
}

function SubConsent({
  checked, onToggle, testId, title, body,
}: {
  checked: boolean; onToggle: () => void;
  testId: string; title: string; body: string;
}) {
  return (
    <button
      type="button" onClick={onToggle}
      data-testid={testId}
      className={`w-full text-left flex items-start gap-3 rounded-lg border px-3.5 py-3 transition ${
        checked ? "bg-[#FF5C00]/10 border-[#FF5C00]/40" : "bg-black/20 border-white/10 hover:border-white/25 hover:bg-black/30"
      }`}
    >
      <div className={`w-4 h-4 rounded mt-0.5 flex-shrink-0 flex items-center justify-center transition ${
        checked ? "bg-[#FF5C00] border border-[#FF5C00]" : "bg-transparent border border-white/30"
      }`}>
        {checked && <CheckIcon className="w-3 h-3 text-white" />}
      </div>
      <div className="min-w-0">
        <div className={`text-sm font-medium ${checked ? "text-white" : "text-white/90"}`}>{title}</div>
        <p className={`text-xs mt-1 leading-relaxed ${checked ? "text-white/85" : "text-white/65"}`}>{body}</p>
      </div>
    </button>
  );
}

function ConsentCard({
  checked, onToggle, icon, title, tagline, testId, confirmLabel, children,
}: {
  checked: boolean; onToggle: () => void;
  icon: React.ReactNode; title: string; tagline: string;
  testId: string; confirmLabel: string; children: React.ReactNode;
}) {
  return (
    <div
      className={`relative rounded-xl border transition mb-3 ${
        checked
          ? "bg-[#FF5C00]/[0.05] border-[#FF5C00]/35"
          : "bg-[var(--mentor-card)] border-[var(--mentor-border)] hover:border-white/20"
      }`}
    >
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition ${
            checked ? "bg-[#FF5C00] text-white" : "bg-[#FF5C00]/10 border border-[#FF5C00]/25 text-[#FF5C00]"
          }`}>
            {checked ? <CheckIcon className="w-4 h-4" /> : icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold leading-tight">{title}</h3>
            <p className="text-xs text-white/60 mt-1 italic">{tagline}</p>
          </div>
        </div>

        <div className="ml-11">
          <div className="mb-3.5">{children}</div>
          <button
            type="button" onClick={onToggle}
            className={`w-full text-left flex items-start gap-3 rounded-lg border px-3.5 py-3 transition ${
              checked ? "bg-[#FF5C00]/10 border-[#FF5C00]/40" : "bg-black/20 border-white/10 hover:border-white/25 hover:bg-black/30"
            }`}
            data-testid={testId}
          >
            <div className={`w-4 h-4 rounded mt-0.5 flex-shrink-0 flex items-center justify-center transition ${
              checked ? "bg-[#FF5C00] border border-[#FF5C00]" : "bg-transparent border border-white/30"
            }`}>
              {checked && <CheckIcon className="w-3 h-3 text-white" />}
            </div>
            <span className={`text-sm leading-snug ${checked ? "text-white" : "text-white/85"}`}>
              {confirmLabel}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
